import React from "react";
import styled from "@emotion/styled";
import { css, keyframes } from "@emotion/react";
import {
  surface,
  border,
  borderRadius,
  layout,
  spacing,
  controlFontSize,
} from "@atlas-int/tokens";

// ─── Shimmer animation ──────────────────────────────────────────────────────

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const shimmerBg = css`
  background: linear-gradient(
    90deg,
    ${surface.disabled} 25%,
    ${surface.hover} 50%,
    ${surface.disabled} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

// ─── Dropdown skeleton (matches TreeSelect Trigger) ─────────────────────────

const DropdownShell = styled.div`
  display: flex;
  align-items: center;
  height: ${layout.controlsHeight};
  padding: 0 ${spacing[2]};
  border: 1px solid ${border.control};
  border-radius: ${borderRadius.base};
  box-sizing: border-box;
  ${shimmerBg}
`;

const FakeChevron = styled.span`
  margin-left: auto;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border-radius: ${borderRadius.base};
  background: ${surface.disabled};
`;

const FakeText = styled.span<{ $width?: string }>`
  width: ${({ $width }) => $width ?? "60%"};
  height: ${controlFontSize.default};
  border-radius: 2px;
  background: ${surface.disabled};
  margin-left: ${spacing[4]};
`;

/**
 * Skeleton that matches the closed TreeSelect / dropdown trigger shape.
 * Use for Location and Product dropdowns while loading.
 */
export const DropdownSkeleton = ({ style, className }: { style?: React.CSSProperties; className?: string }) => (
  <DropdownShell style={style} className={className} aria-hidden>
    <FakeText />
    <FakeChevron />
  </DropdownShell>
);

// ─── Select-box skeleton (matches year SelectBox) ───────────────────────────

const SelectShell = styled.div`
  display: flex;
  align-items: center;
  height: ${layout.controlsHeight};
  border: 1px solid ${border.control};
  border-radius: ${borderRadius.base};
  box-sizing: border-box;
  width: 100%;
  ${shimmerBg}
`;

const FakeSelectText = styled.span`
  flex: 1;
  height: ${controlFontSize.default};
  margin: 0 ${spacing[2]};
  border-radius: 2px;
  background: ${surface.disabled};
`;

const FakeArrow = styled.span`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-right: ${spacing[2]};
  border-radius: 2px;
  background: ${surface.disabled};
`;

/**
 * Skeleton that matches the year SelectBox shape (narrow, with small arrow).
 */
export const SelectSkeleton = ({ style, className }: { style?: React.CSSProperties; className?: string }) => (
  <SelectShell style={style} className={className} aria-hidden>
    <FakeSelectText />
    <FakeArrow />
  </SelectShell>
);

// ─── Toggle-group skeleton (matches ProductClassToggleGroup) ────────────────

const ToggleGroupShell = styled.div`
  display: inline-flex;
  height: ${layout.controlsHeight};
  border: 1px solid ${border.control};
  border-radius: ${borderRadius.base};
  box-sizing: border-box;
  overflow: hidden;
  ${shimmerBg}
`;

const FakeButton = styled.span<{ $width?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $width }) => $width ?? "42px"};
  height: 100%;
  box-sizing: border-box;

  &:not(:last-of-type) {
    border-right: 1px solid ${border.control};
  }
`;

const FakeButtonText = styled.span<{ $width?: string }>`
  width: ${({ $width }) => $width ?? "24px"};
  height: ${controlFontSize.default};
  border-radius: 2px;
  background: ${surface.disabled};
`;

/**
 * Skeleton that matches the attached ButtonGroup shape used for product class toggles.
 * Pass `count` to match the expected number of buttons.
 */
export const ToggleGroupSkeleton = ({
  count = 4,
  buttonWidth,
  style,
  className,
}: {
  count?: number;
  buttonWidth?: string;
  style?: React.CSSProperties;
  className?: string;
}) => (
  <ToggleGroupShell style={style} className={className} aria-hidden>
    {Array.from({ length: count }, (_, i) => (
      <FakeButton key={i} $width={buttonWidth}>
        <FakeButtonText />
      </FakeButton>
    ))}
  </ToggleGroupShell>
);
